import { database } from '../config/firebase';
import { ref, get, set, update } from 'firebase/database';

/**
 * phoneToGroup 테이블의 누락된 엔트리를 수정하는 유틸리티 함수
 */
export async function fixMissingPhoneToGroupEntries(): Promise<void> {
  console.log('🔧 phoneToGroup 누락 엔트리 수정 시작...');
  
  try {
    // accountGroups 테이블에서 모든 그룹 가져오기
    const accountGroupsRef = ref(database, 'accountGroups');
    const accountGroupsSnapshot = await get(accountGroupsRef);
    
    if (!accountGroupsSnapshot.exists()) {
      console.log('❌ accountGroups 데이터가 없습니다.');
      return;
    }

    const accountGroups = accountGroupsSnapshot.val();
    const updates: { [key: string]: any } = {};
    let fixedCount = 0;
    
    // 각 그룹의 멤버들에 대해 phoneToGroup 엔트리 확인 및 생성
    for (const [groupId, groupData] of Object.entries(accountGroups)) {
      const group = groupData as any;
      
      if (group.members && Array.isArray(group.members)) {
        console.log(`📋 그룹 ${groupId} 검사 중... (멤버: ${group.members.length}명)`);
        
        for (const memberPhone of group.members) {
          const phoneToGroupRef = ref(database, `phoneToGroup/${memberPhone}`);
          const phoneSnapshot = await get(phoneToGroupRef);
          
          if (!phoneSnapshot.exists()) {
            // 누락된 엔트리 생성
            console.log(`  ➕ ${memberPhone}에 대한 phoneToGroup 엔트리 생성`);
            
            // primaryPhone인 경우 그룹 생성 시간 사용, 아니면 현재 시간
            const joinedAt = memberPhone === group.primaryPhone 
              ? (group.createdAt || new Date().toISOString())
              : new Date().toISOString();
            
            updates[`phoneToGroup/${memberPhone}`] = {
              groupId: groupId,
              joinedAt: joinedAt
            };
            
            fixedCount++;
          } else {
            console.log(`  ✅ ${memberPhone}는 이미 phoneToGroup에 존재`);
          }
        }
      }
    }
    
    if (Object.keys(updates).length > 0) {
      // 모든 업데이트 한 번에 적용
      await update(ref(database), updates);
      console.log(`✅ phoneToGroup 수정 완료! ${fixedCount}개 엔트리 추가됨`);
    } else {
      console.log('✅ 수정할 엔트리가 없습니다. 모든 데이터가 정상입니다.');
    }
    
  } catch (error) {
    console.error('❌ phoneToGroup 수정 실패:', error);
    throw error;
  }
}

/**
 * phoneToGroup 데이터 무결성 검증
 */
export async function validatePhoneToGroupIntegrity(): Promise<{
  isValid: boolean;
  issues: string[];
}> {
  console.log('🔍 phoneToGroup 무결성 검증 시작...');
  
  const issues: string[] = [];
  
  try {
    // accountGroups와 phoneToGroup 데이터 가져오기
    const [accountGroupsSnapshot, phoneToGroupSnapshot] = await Promise.all([
      get(ref(database, 'accountGroups')),
      get(ref(database, 'phoneToGroup'))
    ]);
    
    const accountGroups = accountGroupsSnapshot.val() || {};
    const phoneToGroup = phoneToGroupSnapshot.val() || {};
    
    // 1. accountGroups의 모든 멤버가 phoneToGroup에 있는지 확인
    for (const [groupId, groupData] of Object.entries(accountGroups)) {
      const group = groupData as any;
      
      if (group.members && Array.isArray(group.members)) {
        for (const memberPhone of group.members) {
          if (!phoneToGroup[memberPhone]) {
            issues.push(`❌ ${memberPhone}이(가) phoneToGroup에 없음 (그룹: ${groupId})`);
          } else if (phoneToGroup[memberPhone].groupId !== groupId) {
            issues.push(`⚠️ ${memberPhone}의 그룹 ID 불일치 (예상: ${groupId}, 실제: ${phoneToGroup[memberPhone].groupId})`);
          }
        }
      }
    }
    
    // 2. phoneToGroup의 모든 엔트리가 유효한 그룹을 가리키는지 확인
    for (const [phone, data] of Object.entries(phoneToGroup)) {
      const phoneData = data as any;
      if (!accountGroups[phoneData.groupId]) {
        issues.push(`⚠️ ${phone}이(가) 존재하지 않는 그룹을 참조 (${phoneData.groupId})`);
      }
    }
    
    const isValid = issues.length === 0;
    
    if (isValid) {
      console.log('✅ phoneToGroup 무결성 검증 통과!');
    } else {
      console.log(`❌ phoneToGroup 무결성 문제 발견: ${issues.length}개`);
      issues.forEach(issue => console.log(issue));
    }
    
    return { isValid, issues };
    
  } catch (error) {
    console.error('❌ 무결성 검증 실패:', error);
    throw error;
  }
}